// Which model reads the document, asked at the moment of asking.
//
// The choice already lives in Settings, and this writes the same field: a
// model picked here is the model from here on, not just for the next run
// (design §14.5). That is deliberate — a per-run override would mean the
// account's answer to "which model does this" depended on where you last
// pressed a button, and the usage table could not be read at a glance.
//
// It opens in the dock's own stack, above the pill, where the search note and
// the find bar already appear. The dock is a fixed column at the foot of the
// screen, so upward is the only direction it can go and there is nothing to
// position.

import { useEffect, useRef } from "react";

import type { AvailableModel } from "../api/client";

interface Props {
  models: AvailableModel[];
  /** What this server would choose if the account asked for nothing. */
  defaultId: string;
  /** What the account has asked for, if anything. */
  current: string | null;
  onChoose: (id: string | null) => void;
  onClose: () => void;
}

export function ModelMenu({ models, defaultId, current, onChoose, onClose }: Props) {
  const menu = useRef<HTMLDivElement | null>(null);

  // The item in use takes focus, so the keyboard starts where the eye does.
  useEffect(() => {
    const checked = menu.current?.querySelector<HTMLElement>('[aria-checked="true"]');
    (checked ?? menu.current?.querySelector<HTMLElement>('[role="menuitemradio"]'))?.focus();
  }, []);

  // A press anywhere else puts it away. Capture, so it is heard before the
  // thing pressed acts on it; and the button that opened this is exempt, or
  // the press that opened the menu would close it on the way back up.
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      const at = e.target as Node | null;
      if (at && (menu.current?.contains(at) || (at as HTMLElement).closest?.("[aria-haspopup]"))) {
        return;
      }
      onClose();
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [onClose]);

  const move = (from: HTMLElement, by: 1 | -1 | "first" | "last") => {
    const items = Array.from(
      menu.current?.querySelectorAll<HTMLElement>('[role="menuitemradio"]') ?? [],
    );
    if (items.length === 0) return;
    const at = items.indexOf(from);
    const to =
      by === "first"
        ? 0
        : by === "last"
          ? items.length - 1
          : (at + by + items.length) % items.length;
    items[to]?.focus();
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const item = e.target as HTMLElement;
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      move(item, 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(item, -1);
    } else if (e.key === "Home") {
      e.preventDefault();
      move(item, "first");
    } else if (e.key === "End") {
      e.preventDefault();
      move(item, "last");
    }
  };

  const item = (id: string | null, label: string, note?: string) => (
    <button
      key={id ?? "default"}
      type="button"
      role="menuitemradio"
      aria-checked={current === id}
      className={`model-item${current === id ? " chosen" : ""}`}
      onClick={() => onChoose(id)}
    >
      <span>{label}</span>
      {note && <span className="model-note">{note}</span>}
    </button>
  );

  return (
    <div
      className="model-menu"
      role="menu"
      aria-label="Which model reviews"
      ref={menu}
      onKeyDown={onKeyDown}
    >
      <p className="model-head">Which model reviews</p>
      {item(null, "This server's choice", defaultId)}
      {models.map((m) =>
        item(m.id, m.label, m.searches ? undefined : "cannot search the web"),
      )}
    </div>
  );
}
